/* eslint-disable no-lonely-if */
import styled, { css } from 'styled-components';

import { Icon } from '@powdr/components/icon';
import { Link } from '@powdr/components/link';
import {
  Components, ThemeProps as Prop, StaticProfiles, CompareCardSides,
  FontFamily,
  FontSize,
} from '@powdr/constants';
import { getFontStyles, themeUtil } from '@powdr/utils';

const CPNT = Components.COMPARE_CARDS;

const featureItemHeight = 55; // px

export const StyledCompareCardInner = styled.div`
  border-radius: 10px;
  border: 1px solid ${() => themeUtil(Prop.LIGHT, CPNT, StaticProfiles.SHADES)};
  ${({ isMobile }) => ((isMobile) ? 'backface-visibility: hidden; position: absolute; width: 100%;' : '')}
  ${({ sideOfCard }) => ((sideOfCard === CompareCardSides.BACK) ? 'transform: rotateY(-180deg);' : '')}
`;

export const CompareCardTitle = styled.div`
  color: ${({ colorProfile }) => themeUtil(Prop.ELEMENT_TXT, CPNT, colorProfile)};
  background-color: ${({ colorProfile }) => themeUtil(Prop.ELEMENT, CPNT, colorProfile)};
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  ${() => getFontStyles(FontFamily.PRIMARY_FONT, FontSize.MED10, undefined, undefined, CPNT, 'title')};
  line-height: 1.1;
  font-weight: bold;
  padding: 15px;
`;

const flippedHidden = css`
  div {
    transition: opacity .2s linear;
    opacity: 0;
  }
`;

const flippedShowing = css`
  div {
    transition: opacity .6s linear;
    opacity: 1;
  }
`;

export const CompareCardBodyContainer = styled.div`
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: ${({ colorProfile }) => themeUtil(Prop.BACKGROUND, CPNT, colorProfile)};

  ${({ sideOfCard, isCardFlipped }) => {
    if (isCardFlipped) {
      if (sideOfCard === CompareCardSides.FRONT) return flippedHidden;
      if (sideOfCard === CompareCardSides.BACK) return flippedShowing;
      return '';
    }

    // if card is not in flipped state
    if (sideOfCard === CompareCardSides.FRONT) return flippedShowing;
    if (sideOfCard === CompareCardSides.BACK) return flippedHidden;
    return '';
  }}
`;

export const HeadlineText = styled.div`
  ${() => getFontStyles(FontFamily.BASE_FONT, FontSize.REG30, undefined, undefined, CPNT, 'headline')};
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  text-align: center;
  line-height: 1.2;
  font-weight: bold;
  padding: 10px 20px 0;
`;

export const FeatureItems = styled.div`
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  padding: 10px 20px 0;
  ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 0.80)};

  ${({
    $maxFeatureItems, isMobile, sideOfCard, $adjustHeight,
  }) => {
    if ($maxFeatureItems !== null) {
      return `
        height: ${$maxFeatureItems * featureItemHeight}px;
      `;
    }

    if ($adjustHeight) {
      return 'height: 425px;';
    }

    return `
      ${(sideOfCard === CompareCardSides.BACK) && 'height: calc(50px + 275px + 175px); padding: 10px 20px;'};
      ${(isMobile) ? 'height: 275px;' : 'height: 350px;'};
    `;
  }}
`;

export const PriceSection = styled.div`
  height: ${({ $adjustHeight }) => (($adjustHeight) ? '100px' : '175px')};
  ${({ sideOfCard, isCardFlipped }) => ((sideOfCard === CompareCardSides.FRONT && isCardFlipped) ? 'pointer-events: none;' : '')}
`;

export const PriceTextWrapper = styled.div`
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  text-align: center;
  margin-bottom: 10px;
`;

export const PriceDescription = styled.div`
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  ${() => getFontStyles(FontFamily.BASE_FONT, undefined, undefined, undefined, CPNT, 'content')};
  margin-bottom: 5px;
`;

export const Price = styled.div`
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  ${() => getFontStyles(FontFamily.BASE_FONT, undefined, undefined, undefined, CPNT, 'content')};
  font-weight: bold;
`;

// checkbox-switch-wrapper
export const ToggleSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 10px;
`;

export const ToggleLabel = styled.div`
  color: ${({ colorProfile }) => themeUtil(Prop.CONTENT, CPNT, colorProfile)};
  display: inline-block;
  ${() => getFontStyles(FontFamily.BASE_FONT, undefined, undefined, undefined, CPNT, 'button-label')};
  padding: 0 5px;
`;

// checkbox-switch
export const SwitchLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  min-width: 52px;
  height: 26px;
`;

// input
export const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked+span {
    background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG, CPNT, colorProfile)};
  }

  &:focus+span {
    box-shadow: 0 0 1px ${({ colorProfile }) => themeUtil(Prop.BTN_BG, CPNT, colorProfile)};
  }

  &:checked+span:before {
    transition: transform .4s ease;
    transform: translateX(24px);
  }
`;

// span.slider.round
export const SliderElement = styled.span`
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG_INACTIVE, CPNT, colorProfile)};
  border-radius: 25px;

  &:before {
    content: "";
    background-color: ${({ colorProfile }) => themeUtil(Prop.BACKGROUND, CPNT, colorProfile)};
    transition: transform .4s ease;
    transform: translateX(0);
    position: absolute;
    height: 18px;
    width: 18px;
    left: 4px;
    bottom: 4px;
    top: 4px;
    border-radius: 50%;
  }
`;

export const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;
  padding-bottom: 15px;
  ${({ sideOfCard, isCardFlipped }) => ((sideOfCard === CompareCardSides.FRONT && isCardFlipped) ? 'pointer-events: none;' : '')}
`;

export const PurchaseButton = styled(Link)`
  ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.REG40, undefined, undefined, CPNT, 'button')};
  width: 160px;
  line-height: 1.1;
  margin-bottom: 10px;
  padding: 12px 16px;
  color: ${({ colorProfile }) => themeUtil(Prop.BTN_TXT, CPNT, colorProfile)};
  background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG, CPNT, colorProfile)};

  &:hover {
    background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, colorProfile)};
    color: ${({ colorProfile }) => themeUtil(Prop.BTN_TXT_HOVER, CPNT, colorProfile)};
  }
`;

export const MoreInfoButton = styled(Link)`
  display: block;
  line-height: 1.1;
  ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.REG40, undefined, undefined, CPNT, 'button')};
  color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};

  &:hover {
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
  }
`;

export const FlipCardButton = styled.div`
  display: flex;
  align-items: center;
  color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};
  ${({ isCardFlipped }) => ((isCardFlipped) ? `
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row-reverse;
    position: absolute;
    bottom: 0;
    left: 0;
    margin: 0 auto;
    width: 100%;
    margin-bottom: 25px;
  ` : '')}

  svg {
    fill: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};
  }

  &:hover {
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};

    svg {
      fill: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
      color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
    }
  }
`;

// .back AND .view-more, adjusting functionality
export const FlipButtonText = styled.div`
  ${({ isCardFlipped }) => ((isCardFlipped)
    ? `font-weight: bold; padding-left: 5px; ${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.MED10, undefined, undefined)};`
    : `${() => getFontStyles(FontFamily.SECONDARY_FONT, FontSize.MED10, undefined, undefined)}; padding-right: 3px;`)};
  color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};

  &:hover {
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
  }
  `;

export const FlipButtonIcon = styled(Icon)`
  ${({ isCardFlipped }) => ((isCardFlipped) ? 'width: 20px; height: 20px;' : 'width: 20px; height: 20px; transform: rotate(180deg);')}

  svg {
    fill: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT, CPNT, colorProfile)};
  }

  &:hover {
    fill: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
    color: ${({ colorProfile }) => themeUtil(Prop.LINK_TXT_HOVER, CPNT, colorProfile)};
  }
`;
